import React, { memo, useContext, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { Flame, TriangleAlert } from "lucide-react";
import { LanguageNameSpaces, SocketTradeData, TradeData } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { DISPLAY_CURRENCIES } from "common/constants";
import PriceLadder from "common/NewLook/PriceLadder.tsx";
import { isTradeBarModel } from "common/NewLook/tradeBarLayout.ts";
import {
  getHedgeLongShare,
  getTradeRowLadder,
} from "common/NewLook/priceLadder.ts";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import useTradeCard from "features/dashboard/common/useTradeCard";
import {
  formatTimeInTrade,
  useLadderPriceFormat,
  useTradeDayPriceChange,
} from "features/trades/NewLook/useTradeLadder.ts";
import { useLadderCaption } from "features/trades/NewLook/useLadderCaption.ts";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import useValueFlash from "common/NewLook/useValueFlash.ts";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const AMOUNT_CLASS = "amount";
const NARROW_LIST_WIDTH = 600;

interface OpenTradesListProps {
  trades: SocketTradeData[];
  /** Every open trade, to pair hedging legs. */
  allTrades: TradeData[] | undefined;
  loading: boolean;
  maxRows: number;
}

/**
 * Open trades as rows you can compare at a glance: what it is, where the
 * price sits between its orders (the same ladder as the trades page), how
 * much is in it and how it is doing.
 */
const OpenTradesList = ({
  trades,
  allTrades,
  loading,
  maxRows,
}: OpenTradesListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);

  return (
    <Table role="table">
      <HeaderRow role="row">
        <span role="columnheader">{tTrades("table.headers.symbol")}</span>
        <span role="columnheader">{tTrades("table.headers.progress")}</span>
        <End role="columnheader" className={AMOUNT_CLASS}>
          {t("open_trades.amount_label")}
        </End>
        <End role="columnheader">{t("open_trades.pnl_label")}</End>
      </HeaderRow>
      {loading && !trades.length
        ? Array.from({ length: 4 }, (_, index) => (
            <Row key={index} as="div" $static={true}>
              <Pair>
                <Skeleton width={28} height={28} radius={14} />
                <Names>
                  <Skeleton width={84} height={12} />
                  <Skeleton width={56} height={10} />
                </Names>
              </Pair>
              <Skeleton width="100%" height={8} radius={4} />
              <End className={AMOUNT_CLASS}>
                <Skeleton width={56} height={12} />
              </End>
              <End>
                <Skeleton width={72} height={12} />
              </End>
            </Row>
          ))
        : trades
            .slice(0, maxRows)
            .map((trade) => (
              <OpenTradeRow
                key={trade.id}
                trade={trade}
                allTrades={allTrades}
              />
            ))}
    </Table>
  );
};

export default memo(OpenTradesList);

interface OpenTradeRowProps {
  trade: SocketTradeData;
  allTrades: TradeData[] | undefined;
}

const OpenTradeRow = memo(({ trade, allTrades }: OpenTradeRowProps) => {
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { socketPnlValue } = useContext(SocketPnlContext) || {};
  const live = socketPnlValue?.[trade.id];
  const tradeData = useMemo(
    () => (live ? { ...trade, ...live } : trade) as TradeData,
    [trade, live]
  );
  const { openTradeExtraDetails, invalidAccountData, isTradeClosed } =
    useTradeCard({ tradeData });
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const hedgeLongShare = getHedgeLongShare(tradeData, allTrades);
  const model = useMemo(
    () => getTradeRowLadder(tradeData, true, hedgeLongShare),
    [tradeData, hedgeLongShare]
  );
  const formatPrice = useLadderPriceFormat(
    tradeData.symbol,
    tradeData.exchange
  );
  const priceChange = useTradeDayPriceChange(tradeData);
  const caption = useLadderCaption(model, formatPrice);
  const hasTradeBar = model.type === "levels" && isTradeBarModel(model);

  const pnl = isBtc ? tradeData.btc_amount_change : tradeData.usd_amount_change;
  const pnlPercent =
    tradeData.graph_data && "profit" in tradeData.graph_data
      ? Number(tradeData.graph_data.profit)
      : undefined;
  const amount = isBtc
    ? tradeData.allocated_amount_btc
    : tradeData.allocated_amount_usd;
  const flash = useValueFlash(pnl);
  const symbol = isBtc ? "₿" : "$";
  const money = (value: number) =>
    `${
      value < 0 ? "−" : value > 0 ? "+" : ""
    }${symbol}${getLocaleStringWithPrecision(Math.abs(value), precision)}`;

  const isShort = tradeData["Long/Short"] === "Short";
  const isHot =
    tradeData.closest_order?.is_hot && !invalidAccountData.isInvalidOrInactive;
  const leverage = tradeData.leverage ? `${tradeData.leverage}x` : undefined;
  const time = formatTimeInTrade(tradeData.graph_data?.time_passed);

  return (
    <Row
      role="row"
      tabIndex={openTradeExtraDetails ? 0 : -1}
      $closed={!!isTradeClosed}
      onClick={openTradeExtraDetails}
      onKeyDown={(event) => {
        if (event.key === "Enter") openTradeExtraDetails?.();
      }}
    >
      <Pair role="cell">
        <IconWithSource
          symbol={tradeData.symbol}
          sourceType={tradeData.source_type}
          source={tradeData.group}
          mainIconSize={28}
          style={ICON_STYLE}
        />
        <Names>
          <SymbolLine>
            <InactiveAccountIcon
              accountId={tradeData.client_id}
              objectType={AccountObjectType.TRADE}
            >
              <SymbolName>{tradeData.symbol}</SymbolName>
            </InactiveAccountIcon>
            {isHot && (
              <Flame
                size={12}
                strokeWidth={2.25}
                color={tokens.stop}
                aria-label={tCommon("labels.hot_trade")}
              />
            )}
          </SymbolLine>
          <SubLine>
            {tradeData["Long/Short"] && (
              <Side style={{ color: isShort ? tokens.down : tokens.up }}>
                {translateOptionName(tradeData["Long/Short"])}
              </Side>
            )}
            {leverage && <span>{leverage}</span>}
            <AccountName title={tradeData.account}>
              {tradeData.account}
            </AccountName>
          </SubLine>
        </Names>
      </Pair>
      <Progress role="cell">
        <PriceLadder
          model={model}
          formatPrice={formatPrice}
          background={tokens.panel}
          priceChange={priceChange}
        />
        {!hasTradeBar && (
          <Caption>
            <CaptionText $warning={caption.isWarning}>
              {caption.isWarning && <TriangleAlert size={11} strokeWidth={2} />}
              {caption.text}
            </CaptionText>
            {time && <span>{time}</span>}
          </Caption>
        )}
      </Progress>
      <End role="cell" className={AMOUNT_CLASS}>
        <Figure>
          {symbol}
          {getLocaleStringWithPrecision(amount || 0, precision)}
        </Figure>
      </End>
      <End role="cell">
        <Figure
          className={flash}
          style={{ color: directionColor(tokens, pnl) }}
        >
          {money(pnl || 0)}
        </Figure>
        {pnlPercent !== undefined && (
          <Percent style={{ color: directionColor(tokens, pnlPercent) }}>
            {pnlPercent > 0 ? "+" : pnlPercent < 0 ? "−" : ""}
            {Math.abs(pnlPercent).toFixed(2)}%
          </Percent>
        )}
      </End>
    </Row>
  );
});

// The source badge overhangs the coin by a few pixels; the row gap does the rest.
const ICON_STYLE = { flex: "none", marginRight: 4 };

const columns = css`
  display: grid;
  grid-template-columns:
    minmax(140px, 1fr) minmax(240px, 2fr) minmax(76px, 0.5fr)
    minmax(96px, 0.6fr);
  column-gap: 20px;
  align-items: center;
  @container (max-width: ${NARROW_LIST_WIDTH}px) {
    grid-template-columns: minmax(110px, 1fr) minmax(200px, 1.8fr) minmax(
        84px,
        0.6fr
      );
    column-gap: 16px;
  }
`;
const Table = styled.div`
  display: flex;
  flex-direction: column;
  container-type: inline-size;
  @container (max-width: ${NARROW_LIST_WIDTH}px) {
    .${AMOUNT_CLASS} {
      display: none !important;
    }
  }
`;
const HeaderRow = styled.div`
  ${columns};
  height: 34px;
  padding: 0 20px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Row = styled.div<{ $static?: boolean; $closed?: boolean }>`
  ${columns};
  min-height: 60px;
  padding: 10px 20px;
  outline: none;
  transition: background-color 120ms, opacity 400ms;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    border-radius: 0 0 12px 12px;
  }
  ${(p) =>
    !p.$static &&
    css`
      cursor: pointer;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${getNewLookTokens(p.theme).focus};
      }
    `}
  ${(p) =>
    p.$closed &&
    css`
      opacity: 0.45;
    `}
`;
const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const SymbolLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;
const SymbolName = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const SubLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Side = styled.span`
  font-weight: 500;
`;
const AccountName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  &::before {
    content: "·";
    margin-right: 6px;
  }
`;
const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;
const Caption = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  ${newLookType.meta};
  font-size: 11px;
  line-height: 14px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const CaptionText = styled.span<{ $warning?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.$warning ? getNewLookTokens(p.theme).stop : "inherit")};
`;
const End = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 0;
  text-align: right;
`;
const Figure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  padding: 0 3px;
  margin: 0 -3px;
  border-radius: 4px;
  white-space: nowrap;
`;
const Percent = styled.span`
  ${newLookType.meta};
  font-weight: 500;
`;
